import React, { memo } from 'react';
import { LabelDescriptionFormProps } from '../../../type';

/**
 * @description
 * A reusable form component that renders input fields for collecting a name and a description.
 * It optionally shows a delete/trash icon for dismissing the modal or section.
 *
 * @Props
 * - nameLabel: Label for the name input.
 * - namePlaceholder: Placeholder text for the name input.
 * - descriptionLabel: Label for the description textarea.
 * - descriptionPlaceholder: Placeholder text for the description textarea.
 * - onNameChange (function): Handler function triggered on change of the name input.
 * - onDescriptionChange (function): Handler function triggered on change of the description textarea.
 * - nameValue (string): The current value of the name input.
 * - descriptionValue (string): The current value of the description.
 * - setContinueBtnDisable (function): Function to set the state of the continue button.
 * - onClose (function): To close the Business info modal.
 * - showIcon (boolean): For to shows a trash icon.
 * - customClassName: Optional custom class name.
 *
 * @returns {JSX.Element} A labeled input and description.
 */

const LabelDescriptionForm: React.FC<LabelDescriptionFormProps> = ({
  nameLabel = 'Name',
  namePlaceholder = 'Enter Name',
  descriptionLabel = 'Description',
  descriptionPlaceholder = 'Enter Description',
  showValidationError = false,
  onNameChange = () => {},
  onDescriptionChange = () => {},
  nameValue = '',
  descriptionValue = '',
  customClassName = '',
  onValidate = () => {},
  nameError = '',
  descError = '',
  showMandatory = false,
}) => (
  <div className={`business-form-container ${customClassName}`}>
    <div className="label-description-conatiner">
      <div className="form-group-label-desc">
        <div className="label-icon">
          <label htmlFor="business-label-input">{`${nameLabel}${
            showMandatory ? '*' : ''
          }`}</label>
        </div>
        <input
          id="business-label-input"
          className={`label-input ${
            showValidationError && nameError ? 'error-name' : ''
          }`}
          type="text"
          placeholder={namePlaceholder}
          value={nameValue}
          onChange={onNameChange}
          onBlur={() => onValidate?.(nameValue, descriptionValue, 'label')}
        />
        {showValidationError && nameError && (
          <p className="error-message">{nameError}</p>
        )}
      </div>

      <div className="form-group-label-desc">
        <label htmlFor="description-input">{`${descriptionLabel}${
          showMandatory ? '*' : ''
        }`}</label>
        <textarea
          id="description-input"
          className={`${showValidationError && descError ? 'error-desc' : ''}`}
          placeholder={descriptionPlaceholder}
          value={descriptionValue}
          onChange={onDescriptionChange}
          rows={5}
          onBlur={() =>
            onValidate?.(nameValue, descriptionValue, 'description')
          }
        />
        {showValidationError && descError && (
          <p className="error-message">{descError}</p>
        )}
      </div>
    </div>
  </div>
);

export default memo(LabelDescriptionForm);
